import React from 'react';
import {
  ArticleText,
  SectionImage,
  ImageTextSection,
  TextContent,
  List,
  ListItem as StyledListItem,
  ArticleSection as StyledArticleSection,
  SectionTitle,
} from './styles/ArticleSectionStyle';
import styled from 'styled-components';
import { BulletListItem } from '../../content/types';

const FullWidthImageContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 2rem 0;
`;

const FullWidthImage = styled(SectionImage)`
  width: 100%;
  height: auto;
  display: block;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

const ImageDescription = styled.span`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  background-color: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
`;

// New styled components for sub-headers section
const SubHeadersContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const SubHeader = styled.div`
  margin: 1.5rem 0 0.75rem;

  h4 {
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    margin: 0.5rem 0;
    text-align: right;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

// New styled component for sub-header with image
const SubHeaderWithImage = styled.div`
  display: flex;
  gap: 2rem;
  margin: 1.5rem 0 0.75rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }

  .sub-header-content {
    flex: 1;

    h4 {
      font-size: 1.2rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      color: ${({ theme }) => theme.colors.text.primary};
    }

    p {
      font-size: 1.1rem;
      line-height: 1.8;
      margin: 0.5rem 0;
      text-align: right;
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }

  .sub-header-image {
    flex: 0 0 300px;
    max-width: 300px;
    position: relative;

    @media (max-width: 768px) {
      flex: none;
      max-width: 100%;
      width: 100%;
    }

    img {
      width: 100%;
      height: auto;
      border-radius: ${({ theme }) => theme.borderRadius.medium};
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .image-caption {
      position: absolute;
      bottom: 0.5rem;
      right: 0.5rem;
      background-color: rgba(255, 255, 255, 0.9);
      color: ${({ theme }) => theme.colors.text.secondary};
      padding: 0.3rem 0.6rem;
      border-radius: ${({ theme }) => theme.borderRadius.small};
      font-size: 0.85rem;
    }
  }
`;

// Replace the SectionLink styled component with a proper button
const SectionButton = styled.a`
  display: inline-flex;
  align-items: center;
  margin-top: 0.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.white};
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  width: fit-content;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  font-size: 0.9rem;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.body};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.main};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  .button-emoji {
    margin-left: 6px;
    font-size: 0.9rem;
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.sm}
      ${({ theme }) => theme.spacing.md};
    font-size: 1rem;
    margin-top: 1rem;
  }
`;

// Bold text styled component
const BoldArticleText = styled(ArticleText)`
  font-weight: 600;
`;

// New styled components for 12Go banner
const BannerContainer = styled.div`
  margin: 2.5rem 0;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 2.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: ${({ theme }) => theme.borderRadius.large || '16px'};
  border: 1px solid ${({ theme }) => theme.colors.border || '#dee2e6'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  gap: 3rem;

  @media (max-width: 968px) {
    flex-direction: column;
    padding: 2rem;
    gap: 2rem;
    text-align: center;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1.5rem;
    gap: 1.5rem;
  }
`;

const BannerTextContent = styled.div`
  flex: 1;
  text-align: right;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 968px) {
    text-align: center;
    align-items: center;
  }
`;

const BannerDescription = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2rem;
  line-height: 1.7;
  max-width: 400px;

  @media (max-width: 968px) {
    max-width: 100%;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const BannerImageContainer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 968px) {
    order: -1;
  }
`;

const BannerImage = styled.img`
  width: 240px;
  height: auto;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 968px) {
    width: 200px;
  }

  @media (max-width: 768px) {
    width: 180px;
  }
`;

const TransportButton = styled.a`
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  border: none;
  border-radius: 14px;
  color: white;
  padding: 14px 28px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.body};
  box-shadow: 0 3px 10px rgba(76, 175, 80, 0.3);
  min-width: 180px;
  justify-content: center;
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(76, 175, 80, 0.4);
    background: linear-gradient(135deg, #45a049 0%, #4caf50 100%);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 968px) {
    width: auto;
    min-width: 200px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 280px;
    padding: 16px 24px;
    font-size: 1rem;
  }
`;

interface ListItem {
  subtitle?: string;
  text: string;
}

interface SubHeaderItem extends ListItem {
  link?: {
    url: string;
    text: string;
    target?: string;
    rel?: string;
  };
  bulletList?: BulletListItem[];
  imageUrl?: string;
  imageDescription?: string;
}

// New styled components for bullet lists
const BulletList = styled.div`
  margin: 1.5rem 0;
`;

const BulletListItemContainer = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  &:first-child {
    padding-top: 0;
  }
  &:last-child {
    padding-bottom: 0;
  }
`;

const BulletTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;

  &::before {
    color: ${({ theme }) => theme.colors.primary.main};
    font-weight: bold;
    margin-right: 0.5rem;
    font-size: 1.2rem;
  }
`;

const BulletDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin: 0.5rem 0;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
`;

const BulletNote = styled.span`
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.9rem;
`;

const BulletItemLink = styled.a`
  display: inline-flex;
  align-items: center;
  margin-top: 0.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.white};
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  width: fit-content;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  font-size: 0.9rem;
  cursor: pointer;
  font-family: ${({ theme }) => theme.fonts.body};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.main};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.3);
  }

  .link-emoji {
    margin-left: 6px;
    font-size: 0.9rem;
  }
`;

interface ArticleSectionProps {
  title: string;
  content:
    | string
    | (string | ListItem | SubHeaderItem)[]
    | string[]
    | BulletListItem[];
  type:
    | 'text'
    | 'list'
    | 'text-image'
    | 'image'
    | 'sub-headers'
    | 'bullet-list';
  imageUrl?: string;
  description?: string;
  isBold?: boolean;
  sectionLink?: {
    url: string;
    text: string;
  };
  is12GoBanner?: boolean;
}

const ArticleSection: React.FC<ArticleSectionProps> = ({
  title,
  content,
  type,
  imageUrl,
  description,
  isBold = false,
  sectionLink,
  is12GoBanner = false,
}) => {
  const renderListItem = (item: string | ListItem, index: number) => {
    if (typeof item === 'string') {
      return (
        <StyledListItem
          key={index}
          dangerouslySetInnerHTML={{ __html: item }}
          style={isBold ? { fontWeight: 700 } : undefined}
        />
      );
    }
    return (
      <StyledListItem key={index}>
        {item.subtitle && <h4>{item.subtitle}</h4>}
        <p
          dangerouslySetInnerHTML={{ __html: item.text }}
          style={isBold ? { fontWeight: 700 } : undefined}
        />
      </StyledListItem>
    );
  };

  const renderSubHeader = (
    item: string | ListItem | SubHeaderItem,
    index: number
  ) => {
    if (typeof item === 'string') {
      return (
        <SubHeader key={index}>
          <p
            dangerouslySetInnerHTML={{ __html: item }}
            style={isBold ? { fontWeight: 700 } : undefined}
          />
        </SubHeader>
      );
    }

    const subHeaderItem = item as SubHeaderItem;

    // If the sub-header item has an image, use the SubHeaderWithImage component
    if (subHeaderItem.imageUrl) {
      return (
        <>
          <SubHeaderWithImage key={index}>
            <div className="sub-header-content">
              {subHeaderItem.subtitle && <h4>{subHeaderItem.subtitle}</h4>}
              <p
                dangerouslySetInnerHTML={{ __html: subHeaderItem.text }}
                style={isBold ? { fontWeight: 700 } : undefined}
              />
            </div>
            <div className="sub-header-image">
              <img
                src={subHeaderItem.imageUrl}
                alt={subHeaderItem.subtitle || 'Image'}
              />
              {(subHeaderItem.imageDescription || subHeaderItem.subtitle) && (
                <span className="image-caption">
                  {subHeaderItem.imageDescription || subHeaderItem.subtitle}
                </span>
              )}
            </div>
          </SubHeaderWithImage>
          {subHeaderItem.bulletList && (
            <BulletList>
              {subHeaderItem.bulletList.map((bulletItem, bulletIndex) =>
                renderBulletListItem(bulletItem, bulletIndex)
              )}
            </BulletList>
          )}
          {subHeaderItem.link ? (
            <>
              {subHeaderItem.link.url ? (
                <BulletItemLink
                  href={subHeaderItem.link.url}
                  target={subHeaderItem.link.target || '_blank'}
                  rel={subHeaderItem.link.rel || 'noopener noreferrer'}
                >
                  <span className="button-emoji">🔗</span>
                  {subHeaderItem.link.text}
                </BulletItemLink>
              ) : null}
            </>
          ) : null}
        </>
      );
    }

    // Default sub-header without image
    return (
      <SubHeader key={index}>
        {subHeaderItem.subtitle && <h4>{subHeaderItem.subtitle}</h4>}
        <p
          dangerouslySetInnerHTML={{ __html: subHeaderItem.text }}
          style={isBold ? { fontWeight: 700 } : undefined}
        />
        {subHeaderItem.bulletList && (
          <BulletList>
            {subHeaderItem.bulletList.map((bulletItem, bulletIndex) =>
              renderBulletListItem(bulletItem, bulletIndex)
            )}
          </BulletList>
        )}
        {subHeaderItem.link ? (
          <>
            {subHeaderItem.link.url ? (
              <BulletItemLink
                href={subHeaderItem.link.url}
                target={subHeaderItem.link.target || '_blank'}
                rel={subHeaderItem.link.rel || 'noopener noreferrer'}
              >
                <span className="button-emoji">🔗</span>
                {subHeaderItem.link.text}
              </BulletItemLink>
            ) : null}
          </>
        ) : null}
      </SubHeader>
    );
  };

  const renderBulletListItem = (item: BulletListItem, index: number) => {
    return (
      <BulletListItemContainer key={index}>
        <BulletTitle>{item.title}</BulletTitle>
        <BulletDescription
          dangerouslySetInnerHTML={{ __html: item.description }}
        />
        {item.note && <BulletNote>({item.note})</BulletNote>}
        {item.link && (
          <BulletItemLink
            href={item.link.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="link-emoji">🔗</span>
            {item.link.text}
          </BulletItemLink>
        )}
      </BulletListItemContainer>
    );
  };

  const TextComponent = isBold ? BoldArticleText : ArticleText;

  return (
    <StyledArticleSection>
      {type !== 'image' && <SectionTitle>{title}</SectionTitle>}

      {type === 'text' && (
        <>
          <TextComponent
            dangerouslySetInnerHTML={{
              __html: content as string,
            }}
          />
          {sectionLink && (
            <SectionButton
              href={sectionLink.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="button-emoji">🔗</span>
              {sectionLink.text}
            </SectionButton>
          )}
        </>
      )}
      {type === 'list' && (
        <List>
          {(content as (string | ListItem)[]).map((item, index) =>
            renderListItem(item, index)
          )}
        </List>
      )}
      {type === 'text-image' && (
        <ImageTextSection>
          <TextContent>
            <TextComponent
              dangerouslySetInnerHTML={{
                __html: content as string,
              }}
            />
          </TextContent>
          <SectionImage src={imageUrl} alt={title} />
        </ImageTextSection>
      )}
      {type === 'image' && (
        <FullWidthImageContainer>
          <FullWidthImage src={imageUrl} alt={title} />
          {title && <ImageDescription>{title}</ImageDescription>}
        </FullWidthImageContainer>
      )}
      {type === 'sub-headers' && (
        <SubHeadersContainer>
          {description && (
            <TextComponent
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          )}
          {(content as (string | ListItem | SubHeaderItem)[]).map(
            (item, index) => renderSubHeader(item, index)
          )}
          {sectionLink && (
            <SectionButton
              href={sectionLink.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="button-emoji">🔗</span>
              {sectionLink.text}
            </SectionButton>
          )}
        </SubHeadersContainer>
      )}
      {type === 'bullet-list' && (
        <div>
          {description && (
            <TextComponent
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          )}
          <BulletList>
            {(content as BulletListItem[]).map((item, index) =>
              renderBulletListItem(item, index)
            )}
          </BulletList>
          {sectionLink && (
            <SectionButton
              href={sectionLink.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="button-emoji">🔗</span>
              {sectionLink.text}
            </SectionButton>
          )}
        </div>
      )}

      {/* 12Go Banner */}
      {is12GoBanner && (
        <BannerContainer>
          <BannerTextContent>
            <BannerDescription>
              הזמינו בקלות אוטובוסים, רכבות, מעבורות והעברות דרך אתר 12Go.
              פלטפורמה מובילה בדרום אסיה עם מחירים תחרותיים ושירות מהימן.
            </BannerDescription>
            <TransportButton
              href="https://12go.asia/?z=11721284"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>🚌</span>
              הזמן דרך 12Go
            </TransportButton>
          </BannerTextContent>
          <BannerImageContainer>
            <BannerImage
              src="/images/affiliate/12go/12go-wide-banner.png"
              alt="12Go - Book trains, buses, ferries, transfers & flights"
            />
          </BannerImageContainer>
        </BannerContainer>
      )}
    </StyledArticleSection>
  );
};

export default ArticleSection;
